import { useTranslation } from 'react-i18next'
import {
  type MetaFunction,
  type LoaderFunctionArgs,
  data,
  Link,
  useLoaderData,
} from 'react-router'
import { Markdown } from '#app/components/markdown.tsx'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { contactEmail, feedbackEmail, siteTitle } from '#app/utils/global.ts'
import { getLocalizedContent, type LocalizedContent } from '#app/utils/i18n.ts'
import { Status } from '#app/utils/status.ts'
import { useOptionalUser } from '#app/utils/user.ts'

interface Faq {
  _id: string
  question: LocalizedContent
  answer: LocalizedContent
}

export async function loader({ context, request }: LoaderFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const currentUserId = await getUserId(request)

  const errorData = {
    title: t('errors.general.title'),
    description: t('errors.general.description'),
  }

  const faqFilter = currentUserId ? 'users == true' : 'marketing == true'
  const faqQuery = `*[_type == 'frequentlyAskedQuestion' && ${faqFilter}]{_id, question, answer, order} | order(order asc)`
  const faqResult = await executeQuery(faqQuery)

  if (faqResult.status !== Status.Success) {
    throw data(errorData, { status: 500 })
  }

  const faq = faqResult.data as Faq[]

  const pageTitle = t('faq.pageTitle')

  return { pageTitle, faq }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.pageTitle ?? 'Error'
  return [{ title: `${pageTitle} | ${siteTitle}` }]
}

export default function FaqRoute() {
  const { faq } = useLoaderData<typeof loader>()

  const { i18n, t } = useTranslation()
  const locale = i18n.language

  const user = useOptionalUser()

  return (
    <>
      <aside>
        <div className="absolute right-0 bottom-0 hidden w-[350px] lg:block">
          <img src="/img/decorative/faq-shapes.svg" alt="" />
        </div>
      </aside>
      <main
        id="main-content"
        className="relative container mb-6 overflow-x-hidden lg:overflow-x-visible"
      >
        <div className="bg-faq-1 absolute top-0 right-[-140px] h-[420px] w-[420px] lg:right-[-218px] lg:h-[708px] lg:w-[708px]" />
        <div className="absolute right-0 bottom-[500px] w-[195px] lg:hidden">
          <img src="/img/decorative/faq-shapes.svg" alt="" />
        </div>
        <div className="relative">
          <h1 className="font-poppins text-4 text-cyan-12 lg:text-6 mt-4 font-bold lg:mt-9">
            {t('faq.title')}
          </h1>
          <div className="relative mt-5 flex flex-col gap-5 lg:mt-9 lg:grid lg:grid-cols-2 lg:gap-x-9 lg:gap-y-6">
            <div className="bg-faq-2 absolute bottom-[-100px] left-[-193px] h-[536px] w-[536px] lg:hidden" />
            {faq.map(question => (
              <div className="relative" key={question._id}>
                <div className="font-poppins text-3 lg:text-4 font-medium">
                  <Markdown
                    text={getLocalizedContent(question.question, locale)}
                  />
                </div>
                <div className="text-1 text-mauve-12 lg:text-2 mt-3 lg:mt-4">
                  <Markdown
                    text={getLocalizedContent(question.answer, locale)}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="relative mt-6 flex flex-col gap-5 lg:grid lg:grid-cols-2 lg:gap-x-9">
            <div className="rounded-4 bg-cyan-11 font-poppins text-3 flex flex-col items-center justify-center px-7 py-5 text-white lg:flex-row lg:justify-between">
              <div className="flex flex-col items-center lg:flex-row">
                <img
                  className="mb-3 w-[50px] flex-none lg:mr-4 lg:mb-0 lg:w-[56px]"
                  src="/img/chat-bubbles.png"
                  alt=""
                />
                {t('faq.anotherQuestion')}
              </div>
              <div className="mt-3 lg:mt-0">
                <Link
                  className="link-button text-2 hover:bg-cyan-12 focus:bg-cyan-12 border-white text-white"
                  to={`mailto:${contactEmail}`}
                >
                  {t('faq.getInTouch')}
                </Link>
              </div>
            </div>
            {user && (
              <div className="rounded-4 bg-purple-7 font-poppins text-3 flex flex-col items-center px-7 py-5 text-white lg:flex-row lg:justify-between">
                <div className="flex flex-col items-center lg:flex-row">
                  <img
                    className="mb-3 w-[50px] flex-none lg:mr-4 lg:mb-0"
                    src="/img/tools.svg"
                    alt=""
                  />
                  {t('faq.helpImprove')}
                </div>
                <div className="mt-3 lg:mt-0">
                  <Link
                    className="link-button text-2 hover:bg-purple-6 focus:bg-purple-6 border-white text-white"
                    to={`mailto:${feedbackEmail}`}
                  >
                    {t('faq.giveFeedback')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  )
}
